import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NbButtonModule, NbCardModule, NbIconModule, NbSpinnerModule } from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import {
  MatrizFrequenciaCatequizandoDto,
  MatrizFrequenciaCelulaDto,
  MatrizFrequenciaTurmaDto,
} from '../../../core/api/matriz-frequencia.dto';
import { FrequenciaService } from '../../../core/api/frequencia.service';

import { mensagemDeErro } from '../../../utils/mensagem-erro';
/**
 * Matriz de frequência da turma: linhas = catequizandos, colunas = datas com chamada.
 * Fonte única: `GET /frequencia/{turmaId}/matriz`.
 */
@Component({
  selector: 'app-frequencia-matriz',
  standalone: true,
  imports: [CommonModule, RouterLink, NbCardModule, NbButtonModule, NbIconModule, NbSpinnerModule],
  templateUrl: './frequencia-matriz.component.html',
  styleUrls: ['./frequencia-matriz.component.scss'],
})
export class FrequenciaMatrizComponent implements OnInit {
  turmaId = '';
  dados: MatrizFrequenciaTurmaDto | null = null;
  carregando = false;
  erro: string | null = null;

  constructor(
    private frequenciaService: FrequenciaService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.turmaId = this.route.snapshot.paramMap.get('turmaId') ?? '';
    this.carregar();
  }

  carregar(): void {
    if (!this.turmaId) {
      this.erro = 'Turma não informada na URL.';
      return;
    }
    this.erro = null;
    this.carregando = true;
    this.frequenciaService
      .carregarMatriz(this.turmaId)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (d) => (this.dados = d),
        error: (err) => {
          this.dados = null;
          this.erro =
            mensagemDeErro(err, 'Não foi possível carregar a matriz de frequência.');
        },
      });
  }

  /** `yyyy-MM-dd` → Date local (evita o recuo de um dia por fuso). */
  paraData(iso: string | undefined): Date | null {
    if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  get datas(): string[] {
    return this.dados?.datas ?? [];
  }

  get catequizandos(): MatrizFrequenciaCatequizandoDto[] {
    return this.dados?.catequizandos ?? [];
  }

  get exibeMissa(): boolean {
    return this.dados?.frequenciaMissa === true;
  }

  /** Presenças da turma naquela data (coluna) — usado no rodapé da matriz. */
  presencasNaData(indice: number): number {
    return this.catequizandos.filter((c) => c.frequencias?.[indice]?.presente === true).length;
  }

  rotuloCelula(c: MatrizFrequenciaCelulaDto | undefined, nome: string): string {
    if (!c) return '';
    const data = this.paraData(c.data)?.toLocaleDateString('pt-BR') ?? c.data;
    const situacao = c.presente ? 'presente' : 'faltou';
    const missa = this.exibeMissa && c.presenteMissa ? ' · presente na missa' : '';
    const semRegistro = c.virtual ? ' (sem registro de chamada para esta pessoa)' : '';
    return `${nome} — ${data}: ${situacao}${missa}${semRegistro}`;
  }

  /** Percentual de presença do catequizando sobre o total de encontros. */
  percentual(c: MatrizFrequenciaCatequizandoDto): number {
    const total = this.dados?.totalEncontros ?? 0;
    if (!total) return 0;
    return Math.round(((c.totalPresencas ?? 0) / total) * 100);
  }

  trackByPessoa = (_: number, c: MatrizFrequenciaCatequizandoDto): string => c.pessoaId;
  trackByData = (_: number, d: string): string => d;
  trackByCelula = (_: number, c: MatrizFrequenciaCelulaDto): string => c.data;

  irFrequenciaDia(): void {
    void this.router.navigate(['/frequencia', this.turmaId]);
  }

  irFaltasConsolidado(): void {
    void this.router.navigate(['/frequencia', this.turmaId, 'faltas-consolidado']);
  }
}
